import React, { Component, ReactElement } from 'react';
import styles from './styles/Timeline.module.scss';

// Interface definitions
interface TimelineData {
  title: string;
  slug: string;
  nodeId: number;
  content: string;
}

interface TimelineEntry {
  title: string;
  fullContent: string;
  index: number;
  year: string;
  hasTitle: boolean;
}

interface TimelineState {
  entries: TimelineEntry[];
  expandedIndex: number | null;
  searchResults: number[];
  loading: boolean;
  highlightedIndex: number | null;
}

interface TimelineProps {
  data: TimelineData;
  containerRef?: React.RefObject<HTMLElement | null>;
}

// Timeline Component using Class Structure
class Timeline extends Component<TimelineProps, TimelineState> {
  private searchInputRef?: React.RefObject<HTMLInputElement | null>;
  private wrapperRef = React.createRef<HTMLDivElement>();
  private hashProcessed = false;

  constructor(props: TimelineProps) {
    super(props);
    this.searchInputRef = React.createRef();
    
    this.state = {
      entries: [],
      expandedIndex: null,
      searchResults: [],
      loading: true,
      highlightedIndex: null,
    };
  }

  componentDidMount(): void {
    this.parseTimelineEntries();
    
    // Listen for external search events from header on the container element
    const containerElement = this.props.containerRef?.current;
    if (containerElement) {
      containerElement.addEventListener('timeline-search', this.handleExternalSearch as EventListener);
    }

    // Listen for hash changes
    window.addEventListener('hashchange', this.handleHashChange);
  }

  componentDidUpdate(prevProps: TimelineProps, prevState: TimelineState): void {
    // After entries are loaded, process initial hash
    if (prevState.loading && !this.state.loading && !this.hashProcessed) {
      this.hashProcessed = true;
      // Add a small delay to ensure DOM is fully updated
      setTimeout(() => {
        this.handleHashChange();
      }, 100);
    }
  }

  componentWillUnmount(): void {
    const containerElement = this.props.containerRef?.current;
    if (containerElement) {
      containerElement.removeEventListener('timeline-search', this.handleExternalSearch as EventListener);
    }

    // Clean up hash change listener
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  // Handle search from external header search box
  handleExternalSearch = (event: any): void => {
    const searchTerm = event.detail?.searchTerm;
    if (!searchTerm) return;

    const { entries } = this.state;
    const results: number[] = [];

    entries.forEach((entry, idx) => {
      const titleMatch = entry.title.toLowerCase().includes(searchTerm);
      const contentMatch = entry.fullContent.toLowerCase().includes(searchTerm);

      if (titleMatch || contentMatch) {
        results.push(idx);
      }
    });

    if (results.length > 0) {
      const firstResultIndex = results[0];
      
      const element = document.getElementById(`timeline-entry-${firstResultIndex}`);
      
      if (element) {
        const rect = element.getBoundingClientRect();
        const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = rect.top + currentScroll - 150;
        
        console.log('Scroll attempt:', {
          currentScroll: currentScroll,
          rectTop: rect.top,
          targetPosition: targetPosition
        });
        
        // Try window.scroll with instant behavior
        window.scroll({ left: 0, top: targetPosition, behavior: 'instant' });
        
        // Log after attempt
        setTimeout(() => {
          console.log('After scroll attempt, window.scrollY:', window.scrollY);
        }, 100);
      }
      
      this.setState(
        {
          expandedIndex: firstResultIndex,
          searchResults: results,
          highlightedIndex: firstResultIndex,
        }
      );

      // Remove highlight after 2 seconds (matches animation duration)
      setTimeout(() => {
        this.setState({ highlightedIndex: null });
      }, 2000);
      
      window.location.hash = `timeline-entry-${firstResultIndex}`;
    }
  };

  // Handle hash navigation (when URL contains #timeline-entry-X)
  handleHashChange = (): void => {
    const hash = window.location.hash.slice(1); // Remove #
    
    if (hash.startsWith('timeline-entry-')) {
      const indexStr = hash.replace('timeline-entry-', '');
      const index = parseInt(indexStr, 10);
      
      if (!isNaN(index) && index >= 0) {
        // Expand the entry immediately
        this.setState({ expandedIndex: index, highlightedIndex: index });
        
        // Remove highlight after 2 seconds (matches animation duration)
        setTimeout(() => {
          this.setState({ highlightedIndex: null });
        }, 2000);
        
        // Retry logic to scroll to element - try multiple times
        const attemptScroll = (attempt: number = 0): void => {
          const element = document.getElementById(hash);
          
          if (element) {
            const rect = element.getBoundingClientRect();
            const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = rect.top + currentScroll - 150;
            
            console.log('Hash scroll attempt (attempt ' + attempt + '):', {
              currentScroll: currentScroll,
              rectTop: rect.top,
              targetPosition: targetPosition,
              elementFound: true
            });
            
            window.scroll({ left: 0, top: targetPosition, behavior: 'instant' });
            
            // Log after attempt
            setTimeout(() => {
              console.log('After hash scroll attempt, window.scrollY:', window.scrollY);
            }, 50);
          } else if (attempt < 5) {
            // Element not found yet, retry after delay
            console.log('Hash element not yet found (attempt ' + attempt + '), retrying...');
            setTimeout(() => attemptScroll(attempt + 1), 200);
          } else {
            console.log('Hash element not found after 5 attempts:', hash);
          }
        };
        
        // Start first attempt immediately
        attemptScroll(0);
      }
    }
  };

  // Extract year from title (e.g., "1000 DF", "ca. 300 DF", "1005/6 DF", "1000 DF Überschrift")
  extractYear = (title: string): { year: string; cleanTitle: string; hasTitle: boolean } => {
    const yearRegex = /^(ca\.\s+)?(\d+(?:\/\d+)?)\s+DF\s*(.*)$/i;
    const match = title.match(yearRegex);
    
    if (match) {
      const year = match[2]; // "1000" or "ca. 300" or "1005/6"
      const cleanTitle = match[3] ? match[3].trim() : ''; // Text after "DF" (or empty)
      return { year, cleanTitle, hasTitle: cleanTitle.length > 0 };
    }
    
    return { year: '', cleanTitle: title, hasTitle: true };
  };

  // Parse timeline content by <h3> tags
  parseTimelineEntries = (): void => {
    const { content } = this.props.data;
    const entries: TimelineEntry[] = [];

    // Create temporary container to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    // Get all h3 elements
    const h3Elements = tempDiv.querySelectorAll('h3');

    if (h3Elements.length === 0) {
      // Fallback: regex parsing
      this.parseTimelineEntriesRegex(content, entries);
    } else {
      // Parse using DOM
      h3Elements.forEach((h3, index) => {
        const fullTitle = h3.textContent || `Eintrag ${index + 1}`;
        const { year, cleanTitle, hasTitle } = this.extractYear(fullTitle);
        const contentContainer = document.createElement('div');

        // Get all siblings after h3 until next h3
        let sibling = h3.nextElementSibling;
        while (sibling && sibling.tagName !== 'H3') {
          contentContainer.appendChild(sibling.cloneNode(true));
          sibling = sibling.nextElementSibling;
        }

        entries.push({
          title: cleanTitle || fullTitle,
          fullContent: contentContainer.innerHTML,
          index,
          year,
          hasTitle,
        });
      });
    }

    // Keep original order - first <h3> is newest (top), last <h3> is oldest (bottom)
    entries.forEach((entry, idx) => {
      entry.index = idx;
    });

    this.setState({
      entries,
      loading: false,
    });
  };

  // Fallback regex parsing for h3 tags
  parseTimelineEntriesRegex = (content: string, entries: TimelineEntry[]): void => {
    const h3Regex = /<h3[^>]*>(.*?)<\/h3>/gi;
    let match;

    while ((match = h3Regex.exec(content)) !== null) {
      const htmlTitle = match[1].replace(/<[^>]+>/g, '');
      const { year, cleanTitle, hasTitle } = this.extractYear(htmlTitle);
      entries.push({
        title: cleanTitle || htmlTitle,
        fullContent: '',
        index: entries.length,
        year,
        hasTitle,
      });
    }

    // Extract content between h3 tags
    const contentParts = content.split(/<h3[^>]*>.*?<\/h3>/i);
    entries.forEach((entry, idx) => {
      entry.fullContent = (contentParts[idx + 1] || '').trim();
    });
  };

  // Toggle entry expansion
  toggleEntry = (index: number): void => {
    this.setState((prevState) => ({
      expandedIndex: prevState.expandedIndex === index ? null : index,
    }));
  };

  // Search functionality
  handleSearch = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;

    const searchTerm = (event.currentTarget.value || '').toLowerCase().trim();
    if (!searchTerm) {
      this.setState({ searchResults: [] });
      return;
    }

    const { entries } = this.state;
    const results: number[] = [];

    entries.forEach((entry, idx) => {
      const titleMatch = entry.title.toLowerCase().includes(searchTerm);
      const contentMatch = entry.fullContent.toLowerCase().includes(searchTerm);

      if (titleMatch || contentMatch) {
        results.push(idx);
      }
    });

    if (results.length > 0) {
      const firstResultIndex = results[0];
      
      const element = document.getElementById(`timeline-entry-${firstResultIndex}`);
      
      if (element) {
        const rect = element.getBoundingClientRect();
        const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = rect.top + currentScroll - 150;
        
        console.log('Scroll attempt:', {
          currentScroll: currentScroll,
          rectTop: rect.top,
          targetPosition: targetPosition
        });
        
        // Try window.scroll with instant behavior
        window.scroll({ left: 0, top: targetPosition, behavior: 'instant' });
        
        // Log after attempt
        setTimeout(() => {
          console.log('After scroll attempt, window.scrollY:', window.scrollY);
        }, 100);
      }
      
      this.setState(
        {
          expandedIndex: firstResultIndex,
          searchResults: results,
        }
      );
      
      window.location.hash = `timeline-entry-${firstResultIndex}`;
    }
  };

  // Close entry
  closeEntry = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    this.setState({ expandedIndex: null });
  };

  // Render single timeline entry
  renderEntry = (entry: TimelineEntry, index: number, totalEntries: number = 0): ReactElement => {
    const { searchResults, highlightedIndex } = this.state;
    const isSearchResult = searchResults.includes(index);
    const isLastEntry = index === totalEntries - 1;
    const isHighlighted = highlightedIndex === index;
    const position = index % 2 === 0 ? styles.timelineLeft : styles.timelineRight;

    return (
      <div
        key={index}
        id={`timeline-entry-${index}`}
        className={`${styles.timelineEntry} ${position} ${isLastEntry ? styles.firstentry : ''}`}
        data-timeline-entry-index={index}
        data-search-result={isSearchResult ? 'true' : 'false'}
      >
        <div className={`${styles.timelineEntryBox}${isHighlighted ? ` ${styles.highlighted}` : ''}`}>
          {entry.hasTitle && <div className={styles.timelineEntryTitle}>{entry.title}</div>}
          <div className={`${styles.timelineEntryContent} ${entry.hasTitle ? '' : styles.noTitle}`}>
            <div
              dangerouslySetInnerHTML={{ __html: entry.fullContent }}
            />
          </div>
        </div>
        <div className={styles.timelineEntryConnector} data-year={entry.year} />
      </div>
    );
  };

  render(): ReactElement {
    const { entries, loading } = this.state;

    // Check if we're in editor mode
    const isEditorMode = /&editor(?:=[^&]*)?$/i.test(window.location.pathname) ||
      new URLSearchParams(window.location.search).has('editor');

    if (loading) {
      return <div className={styles.timelineLoading}>Lade Timeline...</div>;
    }

    // Check if external search box exists in header
    const externalSearchBox = document.querySelector('input[data-timeline-search]') as HTMLInputElement | null;
    const showInternalSearch = !externalSearchBox && !isEditorMode;

    return (
      <div className={styles.timelineWrapper} ref={this.wrapperRef}>
        {showInternalSearch && (
          <div className={styles.timelineSearchBox}>
            <input
              ref={this.searchInputRef}
              type="text"
              className={styles.timelineSearchInput}
              placeholder="Durchsuchen..."
              aria-label="Timeline durchsuchen"
              onKeyDown={this.handleSearch}
            />
          </div>
        )}

        <div className={styles.timelineMain}>
          <div className={styles.timelineAxis} />          
          {entries.map((entry, idx) => this.renderEntry(entry, idx, entries.length))}
        </div>
      </div>
    );
  }
}

export default Timeline;
